export type ExecutionMode = 'sync' | 'async';
/** Platform placement an app's trigger is embedded in. */
export type EmbeddingMode = 'case' | 'message';
export type ExecutionStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'timeout'
  | 'canceled';

export const TERMINAL_STATUSES: ExecutionStatus[] = [
  'completed',
  'failed',
  'timeout',
  'canceled',
];

export interface WindmillApp {
  id: string;
  name: string;
  description: string | null;
  slug: string;
  category: string | null;
  icon: string | null;
  input_schema: JSONSchema;
  /**
   * Admin-configured render order for the input fields, or `null` when no
   * order was ever configured (then the schema's own key order is used).
   */
  field_order: string[] | null;
  result_display: ResultDisplayConfig | null;
  default_execution_mode: ExecutionMode;
  /** Placements this app is embedded in; empty for standalone apps. */
  embedding_modes: EmbeddingMode[];
}

/**
 * Context an embedded app is run from. The backend validates every id (the
 * case must be visible to the workspace, `message_id` must exist) and injects
 * them into the script as `case_id` / `message_id` / `message_composite_id`.
 */
export interface EmbeddingContext {
  mode: EmbeddingMode;
  case_id?: number;
  /** Postgres message id; the backend derives the composite id from it. */
  message_id?: number;
  /** Only for messages with no Postgres id (e.g. search results). */
  message_composite_id?: string;
}

export interface ResultDisplayConfig {
  type: 'json' | 'text' | 'table';
}

export interface JSONSchema {
  type?: string;
  properties?: Record<string, JSONSchemaProperty>;
  required?: string[];
}

export interface JSONSchemaProperty {
  type?: string | string[];
  title?: string;
  description?: string;
  default?: unknown;
  enum?: unknown[];
  format?: string;
  minLength?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  pattern?: string;
  items?: JSONSchemaProperty;
  properties?: Record<string, JSONSchemaProperty>;
  required?: string[];
}

/**
 * Field names in the order they should be rendered.
 *
 * `fieldOrder` comes from the admin and can drift from the live script schema,
 * so it is treated as a preference, never as the source of truth: names that no
 * longer exist in `properties` are skipped, and every property the order does
 * not mention is appended, so an input the user is supposed to fill in can
 * never be dropped. Falls back to the schema's own key order when unset.
 */
export function orderSchemaFields(
  properties: Record<string, JSONSchemaProperty> | undefined,
  fieldOrder?: string[] | null,
): string[] {
  const allKeys = Object.keys(properties || {});
  if (!fieldOrder || fieldOrder.length === 0) return allKeys;

  const known = new Set(allKeys);
  const placed = new Set<string>();
  const ordered: string[] = [];

  for (const key of fieldOrder) {
    if (known.has(key) && !placed.has(key)) {
      placed.add(key);
      ordered.push(key);
    }
  }
  for (const key of allKeys) {
    if (!placed.has(key)) ordered.push(key);
  }

  return ordered;
}

export interface RunResponse {
  execution_id: string;
  status: ExecutionStatus;
  output?: unknown;
  duration_ms?: number | null;
}

export interface ExecutionPollResult {
  id: string;
  status: ExecutionStatus;
  output?: unknown;
  duration_ms?: number | null;
  completed_at?: string | null;
  app_id?: string;
  created_at?: string;
  started_at?: string | null;
  input?: Record<string, unknown>;
}

/**
 * Membership test that tolerates statuses this build does not know about yet
 * (the backend may grow new terminal statuses ahead of the frontend).
 */
export function isTerminalStatus(status: string): boolean {
  return (TERMINAL_STATUSES as string[]).includes(status);
}

export interface ExecutionHistoryItem {
  id: string;
  app_id: string;
  app_name: string;
  status: ExecutionStatus;
  execution_mode: ExecutionMode;
  triggered_by_user_id: number;
  triggered_by_user_name?: string | null;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  created_at: string;
}

export interface ExecutionHistoryResponse {
  objects: ExecutionHistoryItem[];
  page: number;
  size: number;
  total: number;
}
